import { useMutation, useQueryClient } from '@tanstack/react-query';
import { verifyStudentDocument } from '../../api/students';

/** Every view that shows a student's documents or their review status — the
 * Documents tab (and its history), the requirement checklist, the Final
 * Documentation Checklist, the Timeline, and the progress chips elsewhere —
 * so an upload or a decision made on one screen shows on all of them. */
export const invalidateStudentDocumentViews = (
  queryClient: ReturnType<typeof useQueryClient>,
  studentId: number,
) => {
  queryClient.invalidateQueries({ queryKey: ['student-documents', studentId] });
  queryClient.invalidateQueries({ queryKey: ['student-documents-history', studentId] });
  queryClient.invalidateQueries({ queryKey: ['student-document-checklist', studentId] });
  queryClient.invalidateQueries({ queryKey: ['final-document-checklist', studentId] });
  queryClient.invalidateQueries({ queryKey: ['student-timeline', studentId] });
  queryClient.invalidateQueries({ queryKey: ['journey'] });
  queryClient.invalidateQueries({ queryKey: ['final-documentation'] });
  queryClient.invalidateQueries({ queryKey: ['notification-summary'] });
};

/** The one approve/reject action both review screens use (the Documents tab
 * and the Final Documentation Checklist) — the same endpoint, so the same
 * status, audit history and notifications whichever screen it came from. */
export const useDocumentReview = (studentId: number, onDone?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      documentId,
      status,
      reason,
    }: {
      documentId: number;
      status: 'APPROVED' | 'REJECTED';
      reason?: string;
    }) => verifyStudentDocument(documentId, status, reason),
    onSuccess: () => onDone?.(),
    // Refresh on failure too: a 409 means someone else just decided or
    // replaced it, and the screen should show that.
    onSettled: () => invalidateStudentDocumentViews(queryClient, studentId),
  });
};
